import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Edit2, Calendar, Mail, CheckCircle2, UserCheck, Users } from 'lucide-react';
import { useAppointmentTypes } from '@/hooks/useAppointmentTypes';

// Helper function to generate Google Calendar link
const generateGoogleCalendarLink = (appointment, teamName) => {
  const startDate = appointment.date.replace(/-/g, '');
  const startTime = appointment.start_time.replace(':', '') + '00';
  const endTime = appointment.end_time.replace(':', '') + '00';
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: appointment.title,
    dates: `${startDate}T${startTime}/${startDate}T${endTime}`,
    details: `Team: ${teamName}${appointment.description ? '\n\n' + appointment.description : ''}`,
    location: appointment.address || appointment.location || ''
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Helper function to generate Outlook Calendar link
const generateOutlookCalendarLink = (appointment, teamName) => {
  const startDateTime = `${appointment.date}T${appointment.start_time}:00`;
  const endDateTime = `${appointment.date}T${appointment.end_time}:00`;
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: appointment.title,
    startdt: startDateTime,
    enddt: endDateTime,
    body: `Team: ${teamName}${appointment.description ? '\n\n' + appointment.description : ''}`,
    location: appointment.address || appointment.location || ''
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

export default function AppointmentDetailDialog({ open, onClose, appointment, teams, onUpdate, onDelete, onComplete, isAdmin = true, userRole = 'viewer', currentUserId = '' }) {
  const { getTypeInfo } = useAppointmentTypes();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    team_id: '',
    date: '',
    start_time: '',
    end_time: '',
    duration: 60,
    location: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (appointment) {
      setFormData({
        title: appointment.title,
        description: appointment.description || '',
        team_id: appointment.team_id,
        date: appointment.date,
        start_time: appointment.start_time,
        end_time: appointment.end_time,
        duration: appointment.duration,
        location: appointment.location || '',
        address: appointment.address || ''
      });
      setIsEditing(false);
    }
  }, [appointment]);

  useEffect(() => {
    if (formData.start_time && formData.duration) {
      const [hours, minutes] = formData.start_time.split(':').map(Number);
      const startMinutes = hours * 60 + minutes;
      const endMinutes = startMinutes + parseInt(formData.duration);
      const endHours = Math.floor(endMinutes / 60);
      const endMins = endMinutes % 60;
      setFormData(prev => ({
        ...prev,
        end_time: `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`
      }));
    }
  }, [formData.start_time, formData.duration]);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await onUpdate(appointment.id, formData);
      setIsEditing(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      setLoading(true);
      try {
        await onDelete(appointment.id);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleComplete = async () => {
    if (window.confirm('Mark this appointment as completed?')) {
      setCompleting(true);
      try {
        await onComplete(appointment.id);
      } finally {
        setCompleting(false);
      }
    }
  };

  if (!appointment) return null;

  const team = teams.find(t => t.id === appointment.team_id);

  return (
    <Dialog open={open} onOpenChange={onClose} modal={true}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh]" data-testid="appointment-detail-dialog" aria-describedby="appointment-detail-description">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-bold text-[#0f172a] flex items-center justify-between">
            <span>Appointment Details</span>
            {!isEditing && isAdmin && (
              <div className="flex gap-1 sm:gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsEditing(true)}
                  data-testid="edit-appointment-button"
                  className="h-8 w-8 sm:h-10 sm:w-10"
                >
                  <Edit2 size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 sm:h-10 sm:w-10"
                  data-testid="delete-appointment-button"
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            )}
            {!isAdmin && (
              <div className="text-xs sm:text-sm font-normal text-slate-500">
                View Only
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        {!isEditing ? (
          <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
            <div>
              <Label className="text-xs sm:text-sm text-slate-500">Title</Label>
              <p className="text-base sm:text-lg font-semibold text-[#0f172a]" data-testid="appointment-title">{appointment.title}</p>
            </div>

            <div>
              <Label className="text-xs sm:text-sm text-slate-500">Team</Label>
              <div className="flex items-center gap-2 mt-1">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: team?.color }}
                ></div>
                <p className="font-medium text-sm sm:text-base" data-testid="appointment-team">{team?.name}</p>
              </div>
            </div>

            <div>
              <Label className="text-xs sm:text-sm text-slate-500">Type</Label>
              {(() => {
                const typeInfo = getTypeInfo(appointment.appointment_type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div className="flex items-center gap-2 mt-1">
                    <div 
                      className="w-7 h-7 rounded flex items-center justify-center"
                      style={{ backgroundColor: typeInfo.bgColor }}
                    >
                      <TypeIcon size={16} style={{ color: typeInfo.color }} />
                    </div>
                    <p className="font-medium text-sm sm:text-base" data-testid="appointment-type">{typeInfo.label}</p>
                  </div>
                );
              })()}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-500">Date</Label>
                <p className="font-medium" data-testid="appointment-date">{new Date(appointment.date).toLocaleDateString()}</p>
              </div>
              <div>
                <Label className="text-sm text-slate-500">Time</Label>
                <p className="font-medium" data-testid="appointment-time">{appointment.start_time} - {appointment.end_time}</p>
              </div>
            </div>

            <div>
              <Label className="text-sm text-slate-500">Duration</Label>
              <p className="font-medium" data-testid="appointment-duration">{appointment.duration} minutes</p>
            </div>

            {/* Status Section */}
            <div>
              <Label className="text-sm text-slate-500">Status</Label>
              <div className="flex items-center gap-2 mt-1">
                {appointment.status === 'completed' ? (
                  <>
                    <CheckCircle2 size={18} className="text-green-600" />
                    <span className="font-medium text-green-600" data-testid="appointment-status">Completed</span>
                    {appointment.completed_by_name && (
                      <span className="text-sm text-slate-500">
                        by {appointment.completed_by_name}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                    <span className="font-medium text-amber-600" data-testid="appointment-status">Scheduled</span>
                  </>
                )}
              </div>
            </div>

            {appointment.description && (
              <div>
                <Label className="text-sm text-slate-500">Description</Label>
                <p className="text-slate-700 whitespace-pre-wrap" data-testid="appointment-description">{appointment.description}</p>
              </div>
            )}

            {appointment.location && (
              <div>
                <Label className="text-sm text-slate-500">Location</Label>
                <p className="text-slate-700 font-medium" data-testid="appointment-location">{appointment.location}</p>
              </div>
            )}

            {appointment.address && (
              <div>
                <Label className="text-sm text-slate-500">Address</Label>
                <p className="text-slate-700 whitespace-pre-wrap" data-testid="appointment-address">{appointment.address}</p>
                {appointment.address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appointment.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563eb] hover:text-[#1d4ed8] text-sm font-medium inline-flex items-center gap-1 mt-1"
                    data-testid="view-on-map-link"
                  >
                    View on Google Maps
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            )}

            {/* Assignment Section */}
            {(appointment.assigned_to_user_name || appointment.assigned_to_team_name) && (
              <div className="pt-4 border-t border-slate-200">
                <Label className="text-sm text-slate-500 mb-2 block">Assignment</Label>
                <div className="space-y-2">
                  {appointment.assigned_to_user_name && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <UserCheck size={16} className="text-blue-600" />
                      <span className="font-medium">Technician:</span>
                      <span>{appointment.assigned_to_user_name}</span>
                    </div>
                  )}
                  {appointment.assigned_to_team_name && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Users size={16} className="text-purple-600" />
                      <span className="font-medium">Team:</span>
                      <span>{appointment.assigned_to_team_name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Not Assigned Warning */}
            {!appointment.assigned_to_user_name && !appointment.assigned_to_team_name && appointment.status !== 'completed' && (
              <div className="pt-4 border-t border-slate-200">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Not Assigned:</strong> This appointment cannot be marked as complete until assigned to a technician or team.
                  </p>
                </div>
              </div>
            )}

            {/* Add to Calendar Section */}
            <div className="pt-4 border-t border-slate-200">
              <Label className="text-sm text-slate-500 mb-2 block">Add to Calendar</Label>
              <div className="flex gap-2">
                <a
                  href={generateGoogleCalendarLink(appointment, team?.name || 'Unknown')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[#4285f4] hover:bg-[#3367d6] rounded-md transition-colors"
                  data-testid="add-to-google-calendar"
                >
                  <Calendar size={16} />
                  Google Calendar
                </a>
                <a
                  href={generateOutlookCalendarLink(appointment, team?.name || 'Unknown')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[#0078d4] hover:bg-[#106ebe] rounded-md transition-colors"
                  data-testid="add-to-outlook-calendar"
                >
                  <Mail size={16} />
                  Outlook
                </a>
              </div>
            </div>

            {/* Mark as Completed Section - visible for viewers (technicians) when assigned and not yet completed */}
            {userRole === 'viewer' && appointment.status !== 'completed' && onComplete && 
             (appointment.assigned_to_user_id === currentUserId || appointment.assigned_to_team_id) && (
              <div className="pt-4 border-t border-slate-200">
                <Label className="text-sm text-slate-500 mb-2 block">Technician Action</Label>
                <Button
                  onClick={handleComplete}
                  disabled={completing}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-medium"
                  data-testid="mark-complete-button"
                >
                  {completing ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <CheckCircle2 size={18} className="mr-2" />
                      Mark as Completed
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="edit-appointment-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-team">Team</Label>
              <Select
                value={formData.team_id}
                onValueChange={(value) => setFormData({ ...formData, team_id: value })}
                required
              >
                <SelectTrigger data-testid="edit-team-select">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: team.color }}
                        ></div>
                        {team.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                data-testid="edit-appointment-date-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-start-time">Start Time</Label>
                <Input
                  id="edit-start-time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  data-testid="edit-appointment-start-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-duration">Duration (min)</Label>
                <Input
                  id="edit-duration"
                  type="number"
                  min="15"
                  step="15"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                  required
                  data-testid="edit-appointment-duration-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (Optional)</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add details about the appointment"
                rows={3}
                data-testid="edit-appointment-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-location">Location Name (Optional)</Label>
              <Input
                id="edit-location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Conference Room A"
                data-testid="edit-appointment-location-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-address">Address (Optional)</Label>
              <Textarea
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, State, ZIP"
                rows={2}
                data-testid="edit-appointment-address-input"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {isEditing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
                data-testid="cancel-edit-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                disabled={loading}
                data-testid="save-appointment-button"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button type="button" onClick={onClose} data-testid="close-detail-button">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}